import { UA_DEBUG_URL, UA_URL } from '../constants/urls';
import { getUserCookiePreferences } from '../utils/optanon';
import { generateUaScriptContents } from '../utils/scriptUtils';
import Tracker from './Tracker';
import type { initTracker } from './Tracker';

export default class Ua extends Tracker {
  constructor(args: initTracker) {
    super({ ...args, type: 'ua' });
  }

  renderHtmlInHead = (options: unknown) => {
    const { id, name, drymode, logger } = this;

    const gaOptions = options || 'auto';
    const gaScriptUrl = drymode ? UA_DEBUG_URL : UA_URL;

    logger &&
      logger.debug(
        {
          gaScriptUrl,
          gaOptions,
        },
        'renderHtmlInHead',
      );

    return `
            <script>
                ${generateUaScriptContents(id, name, gaScriptUrl, gaOptions)}
            </script>
        `;
  };

  stopDisabledTracker = () => {
    const { isFunctionalCookieBlocked } = getUserCookiePreferences();

    this.logger && this.logger.debug('checking for disabled trackers', isFunctionalCookieBlocked);
    this.logger &&
      this.logger.debug(
        {
          id: this.id,
          isFunctionalCookieBlocked,
        },
        'disabling tracker',
      );
    // @ts-ignore window has read-only properties
    window[`ga-disable-${this.id}`] = isFunctionalCookieBlocked;
  };

  _proxyGa = (...args: Array<unknown>) => {
    if (typeof window !== 'undefined' && window.ga) {
      return window.ga(...args);
    }

    return this.logger?.warn('Ua._proxyGa', 'window.ga is not defined');
  };

  _initializeGa = (options: Record<string, unknown>) => {
    return this._proxyGa('create', this.id, 'auto', this.name, options);
  };

  _command = (...args: Array<unknown>) => {
    const { drymode, name } = this;
    const command = `${name}.${args[0]}`;
    const rest = args.slice(1);

    if (typeof command !== 'string') {
      return this.logger?.error(
        {
          command,
        },
        'Ua._command must be a string',
      );
    }

    this.logger &&
      this.logger.debug(
        {
          command,
          options: rest,
        },
        'command',
      );

    if (!drymode) {
      this._proxyGa(command, ...rest);
    }

    return this.result(true, [command, ...rest]);
  };

  set = (fieldsObject: unknown) => {
    if (!fieldsObject) {
      return this.logger && this.logger.error('Ua.set must have a fieldsObject');
    }

    this._command('set', fieldsObject);
  };

  trackPageView = (
    {
      path = '',
      clickstreamEvent,
      ...data
    }: Record<string, string | unknown> & {
      clickstreamEvent?: { envelope?: { event_type_id?: string } };
    } = {},
    cb: (s: string) => void,
  ) => {
    if (clickstreamEvent?.envelope?.event_type_id) {
      return;
    }

    const trimmedPath = (path as string).trim();

    if (!trimmedPath || trimmedPath === '') {
      this.logger?.error('Ua.trackPageView requires path');

      return this.result(false, {
        path,
        ...data,
      });
    }

    this._command('send', {
      hitType: 'pageview',
      page: trimmedPath,
      ...data,
    });

    if (cb) {
      cb(this.id);
    }

    return this.result(true, {
      hitType: 'pageview',
      page: trimmedPath,
      ...data,
    });
  };

  trackEvent = (
    {
      category = '',
      action = '',
      label = '',
      value = undefined,
      nonInteraction = undefined,
      transport = undefined,
      clickstreamEvent,
      ...args
    }: Record<string, string | boolean | undefined> & {
      clickstreamEvent?: { envelope?: { event_type_id?: string } };
    } = {},
    cb: (s: string) => void,
  ) => {
    if (clickstreamEvent?.envelope?.event_type_id) {
      return;
    }

    const { id, name } = this;

    if (!category || !action) {
      this.logger?.error('Ua.trackEvent requires args.action and args.category');

      return this.result(false, {
        id,
        name,
        category,
        action,
        label,
        value,
        nonInteraction,
        transport,
        ...args,
      });
    }

    const eventObj: Record<string, unknown> = {
      hitType: 'event',
      eventCategory: category,
      eventAction: action,
      eventLabel: undefined,
      eventValue: undefined,
      nonInteraction: undefined,
      transport: undefined,
    };

    if (label) {
      eventObj.eventLabel = label;
    }

    if (typeof value !== 'undefined') {
      if (typeof value !== 'number') {
        this.logger && this.logger.warn('Ua.trackEvent Expected `args.value` arg to be a Number.');
      } else {
        eventObj.eventValue = value;
      }
    }

    if (typeof nonInteraction !== 'undefined') {
      if (typeof nonInteraction !== 'boolean') {
        this.logger && this.logger.warn('Ua.trackEvent `args.nonInteraction` must be a boolean.');
      } else {
        eventObj.nonInteraction = nonInteraction;
      }
    }

    if (typeof transport !== 'undefined') {
      if (typeof transport !== 'string') {
        this.logger && this.logger.warn('Ua.trackEvent `args.transport` must be a string.');
      } else {
        if (['beacon', 'xhr', 'image'].indexOf(transport) === -1) {
          this.logger &&
            this.logger.warn(
              'Ua.trackEvent `args.transport` must be either one of these values: `beacon`, `xhr` or `image`',
            );
        }

        eventObj.transport = transport;
      }
    }

    Object.keys(args)
      .filter((key) => key.substring(0, 'dimension'.length) === 'dimension')
      .forEach((key) => {
        eventObj[key] = args[key];
      });

    Object.keys(args)
      .filter((key) => key.substring(0, 'metric'.length) === 'metric')
      .forEach((key) => {
        eventObj[key] = args[key];
      });

    this._command('send', eventObj);

    if (cb) {
      cb(id);
    }

    return this.result(true, {
      eventObj,
    });
  };

  setCustomDimensions = (nameAndValue: Record<string, string>, cb: (s: string) => void) => {
    const { name, value } = nameAndValue;
    if (cb) {
      cb(this.id);
    }
    return this._command('set', name, value);
  };
}
